import { buildPrivacyPolicy } from './privacyContent.js'

/**
 * Renders a client site's privacy policy from a `PrivacyConfig`.
 *
 * Client sites share no design tokens — each has its own palette — so every
 * colour here comes from the `theme` prop. The defaults are neutral greys that
 * are legible on a light background; pass the site's own classes to make it
 * look native. Structure and copy come from `buildPrivacyPolicy`, so the legal
 * wording stays identical across every site we build.
 *
 * @param {{
 *   config: import('./privacyContent.js').PrivacyConfig,
 *   theme?: Partial<typeof DEFAULT_THEME>,
 *   className?: string,
 * }} props
 */

const DEFAULT_THEME = {
  page: 'bg-white',
  eyebrow: 'text-neutral-500',
  title: 'text-neutral-900',
  body: 'text-neutral-600',
  heading: 'text-neutral-900',
  muted: 'text-neutral-500',
  link: 'text-neutral-900 decoration-neutral-400 hover:decoration-neutral-900',
  border: 'border-neutral-200',
  panel: 'bg-neutral-50',
  bullet: 'before:bg-neutral-400',
  focus: 'focus-visible:outline-neutral-900',
}

/** Renders a string, or an array of strings and { href, label } links. */
function Text({ value, theme }) {
  if (typeof value === 'string') return value
  return (
    <>
      {value.map((part, i) =>
        typeof part === 'string' ? (
          <span key={i}>{part}</span>
        ) : (
          <a
            key={i}
            href={part.href}
            {...(part.href.startsWith('http')
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            className={`rounded-sm underline underline-offset-4 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${theme.link} ${theme.focus}`}
          >
            {part.label}
          </a>
        )
      )}
    </>
  )
}

function Block({ block, theme }) {
  if (block.t === 'h') {
    return (
      <h3 className={`mt-8 text-base font-bold ${theme.heading}`}>
        <Text value={block.text} theme={theme} />
      </h3>
    )
  }

  if (block.t === 'ul') {
    return (
      <ul className="mt-4 flex flex-col gap-2.5">
        {block.items.map((item, i) => (
          <li
            key={i}
            className={`relative pl-6 text-[15px] leading-relaxed before:absolute before:left-0 before:top-[0.6em] before:h-1.5 before:w-1.5 before:rounded-full ${theme.body} ${theme.bullet}`}
          >
            <Text value={item} theme={theme} />
          </li>
        ))}
      </ul>
    )
  }

  if (block.t === 'table') {
    return (
      <div className={`mt-5 overflow-x-auto rounded-xl border ${theme.border}`}>
        <table className="w-full min-w-[540px] border-collapse text-left text-sm">
          <thead>
            <tr className={theme.panel}>
              {block.head.map((cell) => (
                <th
                  key={cell}
                  scope="col"
                  className={`border-b px-4 py-3 text-xs font-bold uppercase tracking-wider ${theme.border} ${theme.heading}`}
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i} className="align-top">
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className={`px-4 py-3 leading-relaxed ${theme.body} ${
                      i < block.rows.length - 1 ? `border-b ${theme.border}` : ''
                    } ${j === 0 ? `font-medium ${theme.heading}` : ''}`}
                  >
                    <Text value={cell} theme={theme} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <p className={`mt-4 text-[15px] leading-relaxed ${theme.body}`}>
      <Text value={block.text} theme={theme} />
    </p>
  )
}

export function PrivacyPolicy({ config, theme: overrides, className = '', homeHref = '/' }) {
  const theme = { ...DEFAULT_THEME, ...overrides }
  const sections = buildPrivacyPolicy(config)
  const { effective, updated, businessName } = config

  return (
    <div className={`${theme.page} ${className}`}>
      <div className="mx-auto max-w-3xl px-5 pb-24 pt-28 md:px-8 md:pt-32">
        {homeHref && (
          <a
            href={homeHref}
            className={`inline-block rounded-sm text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${theme.muted} ${theme.focus}`}
          >
            &larr; Back to {businessName}
          </a>
        )}
        <header className={`mt-8 border-b pb-10 ${theme.border}`}>
          <p className={`text-xs uppercase tracking-[0.25em] ${theme.eyebrow}`}>Legal</p>
          <h1 className={`mt-4 text-3xl font-bold leading-tight md:text-4xl ${theme.title}`}>
            Privacy Policy
          </h1>
          <p className={`mt-4 text-[15px] leading-relaxed ${theme.body}`}>
            How {businessName} collects, uses, and protects personal information
            on this website.
          </p>
          {(effective || updated) && (
            <dl className={`mt-6 flex flex-wrap gap-x-8 gap-y-2 text-xs ${theme.muted}`}>
              {effective && (
                <div className="flex gap-2">
                  <dt className="uppercase tracking-wider">Effective</dt>
                  <dd className={theme.heading}>{effective}</dd>
                </div>
              )}
              {updated && (
                <div className="flex gap-2">
                  <dt className="uppercase tracking-wider">Last updated</dt>
                  <dd className={theme.heading}>{updated}</dd>
                </div>
              )}
            </dl>
          )}
        </header>

        <nav aria-label="On this page" className="mt-10">
          <h2 className={`text-xs uppercase tracking-[0.2em] ${theme.muted}`}>On this page</h2>
          <ol className="mt-4 grid gap-x-8 gap-y-2 sm:grid-cols-2">
            {sections.map((section, i) => (
              <li key={section.id} className="text-sm">
                <a
                  href={`#${section.id}`}
                  className={`rounded-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${theme.body} ${theme.focus}`}
                >
                  <span className={theme.muted}>{String(i + 1).padStart(2, '0')}.</span>{' '}
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {sections.map((section, i) => (
          <section key={section.id} id={section.id} className="mt-14 scroll-mt-24">
            <h2 className={`text-xl font-bold ${theme.heading}`}>
              <span className={`mr-2 ${theme.muted}`}>{String(i + 1).padStart(2, '0')}</span>
              {section.title}
            </h2>
            {section.blocks.map((block, j) => (
              <Block key={j} block={block} theme={theme} />
            ))}
          </section>
        ))}
      </div>
    </div>
  )
}
